/**
 * Die Bausteine jeder Einstellungsseite: Abschnitt, Feldtext, Schalter, Faltung. (15.10.2026)
 *
 * Bis hierher private Methoden von SettingsView (settings-view.ts, 7.200 Zeilen). Sie brauchen
 * nichts aus der Klasse — und die Suche (einst-suche.ts) baut mit denselben Teilen ihre
 * Trefferzeilen. Ein Baustein, ein Ort; die Gestalt steht in style.css („Felder einer Seite“).
 */
import { GERAETE_WERTE } from "../../../shared/einstellungen-felder.js";

/** Eine Überschrift und eine Karte darunter — die Form des Entwurfs. */
export function einstAbschnitt(titel: string): { abschnitt: HTMLElement; karte: HTMLElement } {
  // Ein div, keine section: `.einst-inhalt > section` gibt Karten-Optik,
  // und der Abschnitt TRÄGT eine Karte, er ist keine.
  const abschnitt = document.createElement("div");
  abschnitt.className = "einst-abschnitt";
  const h = document.createElement("h3");
  h.className = "einst-gruppe-titel";
  h.textContent = titel;
  const karte = document.createElement("div");
  karte.className = "einst-karte";
  abschnitt.append(h, karte);
  return { abschnitt, karte };
}

/** Name, ein kurzer Satz darunter — die linke Hälfte jeder Einstellung. */
export function einstText(titel: string, satz: string, feld?: string): HTMLElement {
  const text = document.createElement("span");
  text.className = "einst-feld-text";
  const t = document.createElement("span");
  t.className = "einst-feld-titel";
  t.textContent = titel;
  const k = document.createElement("span");
  k.className = "einst-feld-satz";
  k.textContent = satz;
  text.append(t, k);
  const marke = feld ? geraeteMarke(feld) : null;
  if (marke) text.appendChild(marke);
  return text;
}

/**
 * „Nur auf diesem Gerät" an jedem Schalter eines Gerätewerts. (23.09.2026,
 * Ticket 2511)
 *
 * Martin: *„Werte, die wirklich zum Gerät gehören … stehen als solche
 * gekennzeichnet."* Welche das sind, sagt EINE Liste
 * (shared/einstellungen-felder.ts) — die Marke fragt nur sie. Steht ein
 * Feld nicht mehr darin, verschwindet die Marke von selbst.
 *
 * ⚠️ Ohne sie sahen „Hey Vespera" und „Absenden nach Stille" auf derselben
 *    Seite gleich aus. Am zweiten Gerät folgte das eine, das andere nicht —
 *    für den Nutzer genau der Fehler, den Ticket 2511 behebt.
 */
export function geraeteMarke(feld: string): HTMLElement | null {
  const wert = GERAETE_WERTE.find((w) => w.feld === feld);
  if (!wert) return null;
  const marke = document.createElement("span");
  marke.className = "einst-geraet";
  marke.dataset.feld = feld;
  marke.textContent = "Nur auf diesem Gerät";
  marke.title = wert.grund;
  return marke;
}

/**
 * Ein Schalter als Knopf mit role="switch" — ein Tipp, ein Zustand.
 * Mit `feld` trägt die Zeile den Namen des Felds (`data-feld`): die Suche springt dorthin.
 */
export function einstSchalter(
  titel: string,
  satz: string,
  an: boolean,
  beiWechsel: (an: boolean, zuruecksetzen: () => void) => void,
  feld?: string,
): { zeile: HTMLElement; schalter: HTMLButtonElement } {
  const zeile = document.createElement("div");
  zeile.className = "einst-feld";
  if (feld) zeile.dataset.feld = feld;
  const schalter = schalterKnopf(titel, an, beiWechsel);
  zeile.append(einstText(titel, satz, feld), schalter);
  return { zeile, schalter };
}

/**
 * Nur der Knopf eines Schalters (role="switch", aria-label = Name ohne Anführungszeichen). (18.10.2026)
 * Ein Baustein für jede Zeile mit Schalter: die Felder der Seiten, die Zeilen des Entwickler-Teams und der Gesamtschalter oben.
 */
export function schalterKnopf(
  name: string,
  an: boolean,
  beiWechsel: (an: boolean, zuruecksetzen: () => void) => void,
): HTMLButtonElement {
  const schalter = document.createElement("button");
  schalter.type = "button";
  schalter.className = "einst-schalter";
  schalter.setAttribute("role", "switch");
  schalter.setAttribute("aria-label", name.replace(/[„“]/g, ""));
  schalter.setAttribute("aria-checked", String(an));
  const knopf = document.createElement("span");
  knopf.className = "einst-schalter-knopf";
  schalter.appendChild(knopf);
  schalter.addEventListener("click", () => {
    const neu = schalter.getAttribute("aria-checked") !== "true";
    schalter.setAttribute("aria-checked", String(neu));
    beiWechsel(neu, () => schalter.setAttribute("aria-checked", String(!neu)));
  });
  return schalter;
}

/**
 * Ein ganzer Abschnitt, der eingeklappt beginnt: nur die Überschrift als Zeile, der Inhalt erst nach dem Antippen.
 * (06.10.2026, Martin: „Panel eingeklappt, damit man nicht zu viele Informationen hat“.)
 * `ohneTitel`: die eigene Überschrift des Inhalts entfällt, damit sie nicht zweimal dasteht.
 * `marke` (16.10.2026, Seite „Erweitert“): ein leises Etikett neben dem Titel, etwa „Versuch“ — dieselbe Gestalt wie
 * „Nur auf diesem Gerät“ (`.einst-geraet`).
 */
export function einklappbar(titel: string, inhalt: HTMLElement, ohneTitel = false, marke?: string): HTMLElement {
  const d = document.createElement("details");
  d.className = "einst-einklappbar";
  const s = document.createElement("summary");
  if (marke) {
    const t = document.createElement("span");
    t.className = "einst-einklappbar-titel";
    t.textContent = titel;
    const m = document.createElement("span");
    m.className = "einst-marke";
    m.textContent = marke;
    t.appendChild(m);
    s.appendChild(t);
  } else {
    s.textContent = titel;
  }
  if (ohneTitel) inhalt.querySelector(":scope > h3")?.remove();
  d.append(s, inhalt);
  return d;
}

/** Aufklappbar: die lange Erklärung, die früher über jedem Feld stand. */
export function einstMehr(frage: string, antwort: string): HTMLElement {
  const d = document.createElement("details");
  d.className = "einst-mehr";
  const s = document.createElement("summary");
  s.textContent = frage;
  const p = document.createElement("p");
  p.textContent = antwort;
  d.append(s, p);
  return d;
}
